import { isEqual } from 'lodash'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'

export type RuntimeFieldValueMap = Record<string, string>

export type PassportRuntimeFieldPayloadItem = {
  id: string
  value: string
}

type RuntimeFieldValueLike = {
  id: string | number
  value?: unknown
}

export function createRuntimeFieldValueMap(
  fieldMetadata: ObjectInventoryField[] | null | undefined,
  fieldValues: RuntimeFieldValueLike[] | null | undefined,
): RuntimeFieldValueMap {
  const currentValues = new Map(
    (fieldValues ?? []).map((item) => [String(item.id), toRuntimeFieldValue(item.value)]),
  )

  return Object.fromEntries(
    (fieldMetadata ?? []).map((field) => {
      const fieldId = String(field.id)
      const merged = currentValues.get(fieldId) ?? defaultRuntimeFieldValue(field.default_value)
      return [fieldId, merged]
    }),
  )
}

export function buildPassportRuntimeFieldPayload(
  fieldValues: RuntimeFieldValueMap,
  fieldMetadata: ObjectInventoryField[] | null | undefined,
): PassportRuntimeFieldPayloadItem[] {
  return (fieldMetadata ?? []).map((field) => {
    const fieldId = String(field.id)
    return {
      id: fieldId,
      value: fieldValues[fieldId] ?? defaultRuntimeFieldValue(field.default_value),
    }
  })
}

export function areRuntimeFieldValuesEqual(
  left: RuntimeFieldValueMap,
  right: RuntimeFieldValueMap,
  fieldMetadata: ObjectInventoryField[] | null | undefined,
): boolean {
  return isEqual(
    normalizeRuntimeFieldValues(left, fieldMetadata),
    normalizeRuntimeFieldValues(right, fieldMetadata),
  )
}

export function normalizeRuntimeFieldValues(
  fieldValues: RuntimeFieldValueMap,
  fieldMetadata: ObjectInventoryField[] | null | undefined,
): RuntimeFieldValueMap {
  const supportedFieldIds = (fieldMetadata ?? []).map((field) => String(field.id))
  return Object.fromEntries(
    supportedFieldIds.map((fieldId) => [fieldId, toRuntimeFieldValue(fieldValues[fieldId])]),
  )
}

function defaultRuntimeFieldValue(value: ObjectInventoryField['default_value']): string {
  if (Array.isArray(value)) return value.join(',')
  return value == null ? '' : String(value)
}

function toRuntimeFieldValue(value: unknown): string {
  return value == null ? '' : String(value)
}
